/**
 * /policies/compliance-rules — Mutaba'aa policy hub (Pack 2 v2 UI).
 *
 * Surfaces the 28 statutory floors Mutaba'aa enforces + the
 * dispute-settlement mode policy + the Article 80 sub-category
 * catalogue. Read-only listing today; per-tenant overrides land
 * with Pack 18.
 */


import { PageHeader } from "../components/ui";
import { AgentBadge } from "../components/ui/AgentBadge";

const ARTICLE_80_CATEGORIES = [
  { id: "art_80_assault", labelEn: "Assault" },
  { id: "art_80_fail_perform", labelEn: "Failure to perform" },
  { id: "art_80_misconduct", labelEn: "Misconduct" },
  { id: "art_80_material_damage", labelEn: "Material damage" },
  { id: "art_80_forgery", labelEn: "Forgery" },
  { id: "art_80_probation", labelEn: "During probation" },
  { id: "art_80_absence", labelEn: "Unauthorised absence" },
  { id: "art_80_exploitation", labelEn: "Exploitation of position" },
  { id: "art_80_secrets", labelEn: "Disclosure of secrets" },
  { id: "art_80_court_conviction", labelEn: "Court conviction" },
  { id: "art_80_intoxication", labelEn: "Intoxication" },
];

const ARTICLE_81_GROUNDS = [
  { id: "art_81_no_work_provided", labelEn: "No work provided" },
  { id: "art_81_employer_assault", labelEn: "Employer assault" },
  { id: "art_81_fraud_at_hiring", labelEn: "Fraud at hiring" },
  { id: "art_81_wage_violation", labelEn: "Wage violation" },
  { id: "art_81_unsafe_workplace", labelEn: "Unsafe workplace" },
  { id: "art_81_employer_immorality", labelEn: "Employer immorality" },
  { id: "art_81_treatment_violation", labelEn: "Treatment violation" },
];

const DISPUTE_MODES = [
  {
    id: "pause",
    title: "Pause (Conservative default)",
    desc: "Hold the entire settlement until the dispute resolves. Safest for both sides.",
  },
  {
    id: "split",
    title: "Split — pay undisputed, hold disputed",
    desc: "Disburse the uncontested portion; escrow the disputed amount until ruling.",
  },
  {
    id: "run_undisputed_only",
    title: "Run undisputed only",
    desc: "Pay everything except the disputed line items; reopen later if ruled for employee.",
  },
];

export function CompliancePoliciesPage() {
  return (
    <div className="space-y-8">
      <PageHeader
        title="Compliance Policies"
        subtitle="Mutaba'aa's enforcement contract — what the agent vetoes regardless of autonomy mode."
      />

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <AgentBadge agent="mutabaa" />
          <h3 className="text-base font-semibold">
            Article 80 — disciplinary termination grounds
          </h3>
        </div>
        <p className="text-sm text-text-secondary mb-3">
          Per commandment 46 + 47: every termination event on the bus
          carries a classified sub-category. Article 80 grounds require
          a referenced disciplinary process audit.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {ARTICLE_80_CATEGORIES.map((c) => (
            <div
              key={c.id}
              className="text-xs px-2 py-1 bg-brand-purple/5 border border-brand-purple/20 rounded"
            >
              {c.labelEn}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <AgentBadge agent="mutabaa" />
          <h3 className="text-base font-semibold">
            Article 81 — employer-breach grounds for full-EOS resignation
          </h3>
        </div>
        <p className="text-sm text-text-secondary mb-3">
          When the employee resigns under any of these grounds, EOS is
          paid as if employer-initiated termination.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {ARTICLE_81_GROUNDS.map((c) => (
            <div
              key={c.id}
              className="text-xs px-2 py-1 bg-brand-purple/5 border border-brand-purple/20 rounded"
            >
              {c.labelEn}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <AgentBadge agent="mutabaa" />
          <h3 className="text-base font-semibold">
            Dispute-pending settlement mode (per LE)
          </h3>
        </div>
        <p className="text-sm text-text-secondary mb-3">
          When a Labor Court appeal is filed, Mutaba'aa applies the
          tenant's customer policy — one of three modes.
        </p>
        <div className="space-y-2">
          {DISPUTE_MODES.map((m) => (
            <div
              key={m.id}
              className="p-3 border border-border-subtle rounded"
            >
              <div className="font-medium text-sm">{m.title}</div>
              <div className="text-xs text-text-secondary mt-1">
                {m.desc}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
